import { Carousel } from 'antd';
import moment from 'moment-timezone';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  FEATURED_COLLECTION_CAROUSEL,
  staticUiLabels
} from '../../../../common/constants';
import { useApp } from '../../../../providers/AppContext';
import FeaturedCollectionCarouselLoading from '../../../skeleton/FeaturedCollectionCarouselLoading';
import FeaturedCollection from './FeaturedCollection';

const isDebugMode = false;

const baseSettings = {
  accessibility: true,
  adaptiveHeight: true,
  arrows: true,

  centerMode: true,
  centerPadding: '0',

  dots: true,
  draggable: true,
  easing: 'cubic-bezier(0.4, 0.0, 0.2, 1)',

  infinite: false,

  slidesToScroll: 1,
  slidesToShow: 1,
  speed: 600,

  variableWidth: true,
  waitForAnimate: true,
  responsive: [
    {
      breakpoint: 1024,
      settings: {
        centerPadding: '0',
        arrows: true,
        dots: true
      }
    },
    {
      breakpoint: 767,
      settings: {
        slidesToShow: 1,
        slidesToScroll: 1,
        centerMode: false,
        centerPadding: '0',
        dots: true,
        arrows: true,
        variableHeight: true,
        fade: false
      }
    }
  ]
};

function FeaturedCollectionCarousel({
  data = [],
  showCta,
  autoAdvanceEnabled,
  autoAdvanceInterval,
  autoAdvanceResumeDelay
}) {
  const {
    state: { uiLabels }
  } = useApp();

  const enableAutoAdvance = autoAdvanceEnabled;

  const AUTO_ADVANCE_INTERVAL_SEC =
    typeof autoAdvanceInterval === 'number'
      ? autoAdvanceInterval
      : FEATURED_COLLECTION_CAROUSEL.AUTO_ADVANCE_INTERVAL_SEC;

  const RESUME_DELAY_SEC =
    typeof autoAdvanceResumeDelay === 'number'
      ? autoAdvanceResumeDelay
      : FEATURED_COLLECTION_CAROUSEL.RESUME_DELAY_SEC;

  const AUTO_ADVANCE_SEC = AUTO_ADVANCE_INTERVAL_SEC.toFixed(1);
  const RESUME_DELAY_SEC_FORMATTED = RESUME_DELAY_SEC.toFixed(1);

  const timerRef = useRef({ resume: null, interaction: null });
  const userInteractionRef = useRef(false);
  const isHoveringRef = useRef(false);

  const [autoplayEnabled, setAutoplayEnabled] = useState(false);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  const sliderContainerRef = useRef(null);
  const carouselRef = useRef(null);

  const [lastEvent, setLastEvent] = useState('INIT');
  const [elapsed, setElapsed] = useState(0);
  const playStartRef = useRef(null);
  const pauseStartRef = useRef(null);

  useEffect(() => {
    if (!isDebugMode || !enableAutoAdvance) return;
    const id = setInterval(() => {
      if (playStartRef.current !== null) {
        setElapsed(+((Date.now() - playStartRef.current) / 1000).toFixed(1));
      } else if (pauseStartRef.current !== null) {
        setElapsed(+((Date.now() - pauseStartRef.current) / 1000).toFixed(1));
      }
    }, 100);
    return () => clearInterval(id);
  }, [enableAutoAdvance]);

  useEffect(() => {
    if (typeof window === 'undefined' || !enableAutoAdvance) return;
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(mediaQuery.matches);
    const handleChange = (e) => setPrefersReducedMotion(e.matches);
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [enableAutoAdvance]);

  const clearAllTimers = useCallback((source) => {
    if (timerRef.current.resume) clearTimeout(timerRef.current.resume);
    if (timerRef.current.interaction)
      clearTimeout(timerRef.current.interaction);
    timerRef.current = { resume: null, interaction: null };
    if (isDebugMode) {
      setLastEvent(`CLEARED (${source})`);
    }
  }, []);

  useEffect(() => {
    return () => {
      clearAllTimers('UNMOUNT');
    };
  }, [clearAllTimers]);

  const pauseAutoplay = useCallback(
    (source) => {
      if (!enableAutoAdvance) return;
      setAutoplayEnabled(false);
      clearAllTimers(source);
      if (isDebugMode) {
        playStartRef.current = null;
        pauseStartRef.current = Date.now();
        setElapsed(0);
      }
    },
    [clearAllTimers, enableAutoAdvance]
  );

  const handleInteraction = useCallback(
    (source) => {
      if (!enableAutoAdvance) return;
      userInteractionRef.current = true;
      pauseAutoplay(source);

      timerRef.current.interaction = setTimeout(() => {
        userInteractionRef.current = false;
        if (!prefersReducedMotion && !isHoveringRef.current) {
          const slider = carouselRef.current?.innerSlider;
          if (slider) {
            playStartRef.current = Date.now();
            setElapsed(0);
            carouselRef.current.next();
            slider.autoPlay();
          }
          setAutoplayEnabled(true);
          if (isDebugMode) setLastEvent(`RESUMED (${source}_END)`);
        }
      }, RESUME_DELAY_SEC * 1000);
    },
    [pauseAutoplay, enableAutoAdvance, RESUME_DELAY_SEC, prefersReducedMotion]
  );

  useEffect(() => {
    const container = sliderContainerRef.current;
    if (!container || !enableAutoAdvance) return;

    const onFocusIn = () => handleInteraction('FOCUS');
    const onFocusOut = (e) => {
      if (!container.contains(e.relatedTarget) && !userInteractionRef.current) {
        clearAllTimers('BLUR');
        const slider = carouselRef.current?.innerSlider;
        if (slider) {
          playStartRef.current = Date.now();
          setElapsed(0);
          slider.autoPlay();
        }
        setAutoplayEnabled(true);
      }
    };
    const onClick = () => handleInteraction('CLICK');
    const onKeyDown = (e) => {
      if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
        handleInteraction('KEY_PRESS');
      }
    };

    container.addEventListener('focusin', onFocusIn, true);
    container.addEventListener('focusout', onFocusOut, true);
    container.addEventListener('click', onClick, true);
    container.addEventListener('keydown', onKeyDown, true);

    return () => {
      container.removeEventListener('focusin', onFocusIn, true);
      container.removeEventListener('focusout', onFocusOut, true);
      container.removeEventListener('click', onClick, true);
      container.removeEventListener('keydown', onKeyDown, true);
    };
  }, [handleInteraction, clearAllTimers, enableAutoAdvance]);

  useEffect(() => {
    if (enableAutoAdvance && !prefersReducedMotion && data?.length > 1) {
      setAutoplayEnabled(true);
    }
  }, [prefersReducedMotion, data?.length, enableAutoAdvance]);

  useEffect(() => {
    if (!enableAutoAdvance) return;
    const id = setTimeout(() => {
      const slider = carouselRef.current?.innerSlider;
      if (!slider) return;
      if (autoplayEnabled) {
        playStartRef.current = Date.now();
        setElapsed(0);
        slider.autoPlay();
      } else {
        playStartRef.current = null;
        setElapsed(0);
        slider.pause('paused');
      }
    }, 0);
    return () => clearTimeout(id);
  }, [autoplayEnabled, enableAutoAdvance]);

  const newShowWeeks = useMemo(() => {
    const rawWeeks =
      uiLabels?.FEATURED_COLLECTION_NEW_SHOW_BADGE_WEEKS?.value ??
      staticUiLabels.FEATURED_COLLECTION_NEW_SHOW_BADGE_WEEKS;
    const parsed = Number.parseInt(rawWeeks, 10);
    return Number.isNaN(parsed) ? 2 : parsed;
  }, [uiLabels?.FEATURED_COLLECTION_NEW_SHOW_BADGE_WEEKS?.value]);

  const dataWithIsNewShow = useMemo(() => {
    return data?.map((item) => {
      const isNewShow = (() => {
        const publishedAt = item?.publishedAt;
        if (!publishedAt) return false;
        const publishedDate = moment(publishedAt).startOf('day');
        const weeksAgo = moment()
          .subtract(newShowWeeks, 'weeks')
          .startOf('day');
        return !publishedDate.isBefore(weeksAgo);
      })();
      return { ...item, isNewShow };
    });
  }, [data, newShowWeeks]);

  return (
    <section
      className="featurecollection-slider"
      ref={sliderContainerRef}
      onMouseEnter={() => {
        if (enableAutoAdvance && window.innerWidth >= 768) {
          isHoveringRef.current = true;
          handleInteraction('HOVER');
        }
      }}
      onMouseLeave={() => {
        if (enableAutoAdvance && window.innerWidth >= 768) {
          isHoveringRef.current = false;
          handleInteraction('HOVER_END');
        }
      }}
    >
      {isDebugMode && enableAutoAdvance && (
        <div className="debug-panel">
          <div>
            {'status: '}
            {autoplayEnabled ? 'playing' : 'paused'}
          </div>
          <div>
            {autoplayEnabled ? 'playing: ' : 'paused: '}
            {elapsed.toFixed(1)}
            {autoplayEnabled
              ? `s / ${AUTO_ADVANCE_SEC}s`
              : `s / ${RESUME_DELAY_SEC_FORMATTED}s`}
          </div>
          <div>
            {'slide: '}
            {carouselRef.current?.innerSlider?.state?.currentSlide ?? '?'}
          </div>
          <div>
            {'event: '}
            {lastEvent}
          </div>
          <div>
            {'resume delay: '}
            {RESUME_DELAY_SEC_FORMATTED}s
          </div>
        </div>
      )}

      <Carousel
        ref={carouselRef}
        {...baseSettings}
        autoplay={enableAutoAdvance}
        autoplaySpeed={AUTO_ADVANCE_INTERVAL_SEC * 1000}
      >
        {dataWithIsNewShow?.map((item, index) => (
          <div
            key={item.id}
            aria-label={`Slide ${index + 1} of ${dataWithIsNewShow.length}`}
          >
            <FeaturedCollection {...item} showCta={showCta} isCarousel />
          </div>
        ))}
      </Carousel>
    </section>
  );
}

FeaturedCollectionCarousel.Loading = function Loading() {
  return <FeaturedCollectionCarouselLoading settings={baseSettings} />;
};

export default FeaturedCollectionCarousel;
